import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import classNames from 'classnames'

import { RequirePermissions, useRight } from '@/libraries/access-control'
import { Button } from '@/libraries/ui'
import { EventSlide, EventSlideProps, startSlideId, useEventSlides } from '@/components/event/EventSlide'
import { LoadingState } from '@/components/LoadingState'
import { SlideContainer } from '@/components/Slide'
import { ProjectionSettingsButton } from '@/components/Slide/ProjectionSettingsButton'
import { useProjectionMargins } from '@/components/Slide/useProjectionMargins'
import { useSlideshowNavigation } from '@/components/Slide/useSlideshowNavigation'
import { useT } from '@/i18n'
import { useOnKeydown } from '@/utils/useOnKeydown'

import { ProgramTitleSelector } from './-components/ProgramTitleSelector'
import { SlideEditor } from './-components/SlideEditor'
import { Event, useBallProgramQuery } from './-components/useBallProgramQuery'

import './BallProgram.scss'

export const Route = createFileRoute(
  '/events/$eventId/{-$eventVersionId}/ball-program/{-$slideId}',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { eventId, eventVersionId } = Route.useParams()
  const { event, slides, refetch, loadingState } = useBallProgram(eventId, eventVersionId)
  const t = useT('common')

  const [isEditing, setEditing] = useState(false)
  const onToggleEditing = () => setEditing(e => !e && canEdit)
  const canEdit = useRight('events:modify', { entityId: eventId })

  const { slideId: currentSlideId = startSlideId } = useParams({ from: '/events/$eventId/{-$eventVersionId}/ball-program/{-$slideId}' })

  useOnKeydown({
    r: refetch as () => unknown,
    e: onToggleEditing,
  })

  if (!event) return <LoadingState {...loadingState} refetch={refetch} />

  const slideIndex = (i => i >= 0 ? i : 0)(slides.findIndex(s => s.id === currentSlideId))
  const slide = slides[slideIndex]

  return <div className={classNames('ball-program-container', { 'is-editing': isEditing })}>
    <BallProgramSlideView
      slides={slides}
      currentSlideIndex={slideIndex}
      event={event}
      isEditing={isEditing}
      onToggleEditing={onToggleEditing}
    />
    <RequirePermissions requireRight="events:modify" entityId={eventId}>
      <div className="editor">
        <Button className="close" minimal text={t('closeEditor')} iconOnly icon="cross" onClick={() => setEditing(false)} />
        <SlideEditor slide={slide} eventId={eventId} eventVersionId={eventVersionId} eventProgram={event?.program} />
      </div>
    </RequirePermissions>
  </div>
}

function useBallProgram(eventId: string, eventVersionId?: string | null) {
  const { data, refetch, ...loadingState } = useBallProgramQuery({ eventId, eventVersionId })
  const event = data?.event
  const slides = useEventSlides(event?.program)

  return { event, slides, loadingState, refetch }
}

export function BallProgramSlideView(
  { slides, currentSlideIndex, event, isEditing, onToggleEditing }: {
    slides: EventSlideProps[]
    currentSlideIndex: number
    event: Event
    onToggleEditing: () => unknown
    isEditing: boolean
  },
) {
  const navigate = useNavigate()
  const goToSlide = (slideId: string) => navigate({
    to: '/events/$eventId/{-$eventVersionId}/ball-program/{-$slideId}',
    params: { eventId: event._id, eventVersionId: event._versionId ?? undefined, slideId },
  })
  const slide = slides[currentSlideIndex]
  const previousSlide = slides[currentSlideIndex - 1]
  const nextSlide = slides[currentSlideIndex + 1]
  const { swipeHandlers } = useSlideshowNavigation({
    slides, currentSlideId: slide.id, onChangeSlide: slide => goToSlide(slide.id),
  })
  const canEdit = useRight('events:modify', { entityId: event._id })
  const t = useT('common')

  const { margins, setMargin, resetMargins } = useProjectionMargins()
  const fullscreen = !isEditing || !canEdit

  return <SlideContainer fullscreen={fullscreen} projectionMargins={margins} {...swipeHandlers}>
    <div className="controls">
      <ProgramTitleSelector value={slide.parentId ?? slide.id} onChange={id => goToSlide(id)}
        program={event.program} />
      {fullscreen && <ProjectionSettingsButton margins={margins} onChange={setMargin} onReset={resetMargins} />}
      <Button requireRight="events:modify" entityId={event._id} minimal text={t(isEditing ? 'closeEditor' : 'edit')} iconOnly icon="edit" onClick={onToggleEditing} />
    </div>
    <EventSlide {...slide} key={slide.id} eventProgram={event.program} />
    {nextSlide && <EventSlide {...nextSlide} key={nextSlide.id} eventProgram={event.program} invisible />}
    {previousSlide && <EventSlide {...previousSlide} key={previousSlide.id} eventProgram={event.program} invisible />}
  </SlideContainer>
}
